import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { TrialService } from "@/lib/subscription/trial.service";
import { useAuth } from "@/lib/contexts/auth.context";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { useCoinBalance } from "@/lib/coin/coin.store";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface TrialCapEventDetail {
	remainingCoins: number;
	windowResetAt?: string;
}

function formatRemaining(windowResetAt?: string | null): {
	hours: number;
	minutes: number;
} | null {
	if (!windowResetAt) return null;
	const diff = new Date(windowResetAt).getTime() - Date.now();
	if (Number.isNaN(diff) || diff <= 0) return { hours: 0, minutes: 0 };
	const totalMinutes = Math.ceil(diff / 60000);
	return {
		hours: Math.floor(totalMinutes / 60),
		minutes: totalMinutes % 60,
	};
}

/**
 * Global cap-hit modal that opens on any `trial:capHit` window event
 * (dispatched by BaseApiClient when the backend returns 429 + TRIAL_RATE_LIMIT).
 *
 * Three actions:
 * - Activate Creator now -> POST stripe/end-trial-now (immediate billing)
 * - Switch to Starter   -> POST stripe/switch-trial-to-starter (no trial, EUR 8.99/mo)
 * - Wait Xh Ym          -> dismiss
 *
 * Reads the coin store for the countdown so it stays in sync with the
 * sidebar sprint indicator and the server-side cap helper.
 */
export function TrialCapModal() {
	const { t } = useTranslation();
	const { refreshUserProfile } = useAuth();
	const { trialWindow, refresh: refreshBalance } = useCoinBalance();
	const [open, setOpen] = useState(false);
	const [windowResetAt, setWindowResetAt] = useState<string | undefined>();
	const [submitting, setSubmitting] = useState<"activate" | "switch" | null>(
		null,
	);

	useEffect(() => {
		const handler = (event: Event) => {
			const detail = (event as CustomEvent<TrialCapEventDetail>).detail;
			setWindowResetAt(detail?.windowResetAt);
			setOpen(true);
			AnalyticsService.track("trial_cap_hit", {
				remaining_coins: detail?.remainingCoins,
				window_reset_at: detail?.windowResetAt,
			});
			AnalyticsService.track("trial_cap_upgrade_modal_viewed", {
				remaining_coins: detail?.remainingCoins,
				window_reset_at: detail?.windowResetAt,
			});
		};
		window.addEventListener("trial:capHit", handler);
		return () => window.removeEventListener("trial:capHit", handler);
	}, []);

	const resolvedResetAt = trialWindow?.windowResetAt ?? windowResetAt;
	const remaining = formatRemaining(resolvedResetAt);

	const handleActivate = async () => {
		try {
			setSubmitting("activate");
			AnalyticsService.track("trial_cap_upgrade_clicked", {
				variant: "activate_creator",
			});
			await TrialService.endTrialNow();
			toast.success(t("trial.cap.toasts.activated"));
			await refreshUserProfile();
			await refreshBalance();
			setOpen(false);
		} catch (error) {
			console.error("Failed to activate Creator now:", error);
			toast.error(t("trial.cap.toasts.activateFailed"));
		} finally {
			setSubmitting(null);
		}
	};

	const handleSwitchToStarter = async () => {
		try {
			setSubmitting("switch");
			AnalyticsService.track("trial_cap_upgrade_clicked", {
				variant: "switch_to_starter",
			});
			await TrialService.switchTrialToStarter();
			toast.success(t("trial.cap.toasts.switchedToStarter"));
			await refreshUserProfile();
			await refreshBalance();
			setOpen(false);
		} catch (error) {
			console.error("Failed to switch to Starter:", error);
			toast.error(t("trial.cap.toasts.switchFailed"));
		} finally {
			setSubmitting(null);
		}
	};

	const handleWait = () => {
		AnalyticsService.track("trial_cap_waited", {
			window_reset_at: resolvedResetAt,
		});
		setOpen(false);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) handleWait();
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Sparkles className="h-5 w-5 text-emerald-500" />
						{t("trial.cap.title")}
					</DialogTitle>
					<DialogDescription>
						{t("trial.cap.description", { limit: trialWindow?.limit ?? 400 })}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-3 mt-2">
					<Button
						type="button"
						onClick={handleActivate}
						disabled={submitting !== null}
					>
						{submitting === "activate" ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								{t("common.processing")}
							</>
						) : (
							t("trial.cap.cta.activateCreator")
						)}
					</Button>

					<Button
						type="button"
						variant="secondary"
						onClick={handleSwitchToStarter}
						disabled={submitting !== null}
					>
						{submitting === "switch" ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								{t("common.processing")}
							</>
						) : (
							t("trial.cap.cta.switchStarter")
						)}
					</Button>

					<Button
						type="button"
						variant="outline"
						onClick={handleWait}
						disabled={submitting !== null}
					>
						{remaining
							? t("trial.cap.cta.waitWithTime", {
									hours: remaining.hours,
									minutes: remaining.minutes,
								})
							: t("trial.cap.cta.wait")}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
